import React, { useMemo } from "react"
import { graphql, navigate } from "gatsby"
import tw from "twin.macro"
import styled from "@emotion/styled"

import { Layout, SEO, ProductGrid, Pagination } from "../components"
import { DealsCategoryQuery, SitePageContext } from "../../graphql-types"
import { IProduct, irregularPluralWords } from "../helpers"
import { addIrregularRule, plural } from "pluralize"

interface Props {
  data: DealsCategoryQuery
  pageContext: SitePageContext
  location: Location
}

const createURL = (categoryName: string) => (pageNumber: number) => {
  if (pageNumber == 1) {
    return `/deals/${categoryName}/`
  } else {
    return `/deals/${categoryName}/${pageNumber}`
  }
}

const getCategoryTitle = (categoryName: string) => {
  if (categoryName in irregularPluralWords) {
    addIrregularRule(categoryName, categoryName)
  }

  const newCategoryName = plural(categoryName)

  return newCategoryName
}

const CategoryTemplate: React.FC<Props> = ({
  data,
  location,
  pageContext: { numberOfPages, humanPageNumber, category: categoryName },
}) => {
  const products = useMemo(() => {
    return data.products.edges.map((elem: any) => ({
      ...elem.node,
      ...elem.node.frontmatter,
    }))
  }, [data])

  const onPageChange = (pageNumber: number) => {
    const pageToNavigate = createURL(categoryName)(pageNumber)
    navigate(pageToNavigate)
  }

  return (
    <Layout location={location} title={""}>
      <SEO title={`Deals: ${categoryName}`} />
      <Styles.Wrapper>
        <Styles.Title>
          Deals in{" "}
          <Styles.Capitalize>
            {getCategoryTitle(categoryName)}
          </Styles.Capitalize>
        </Styles.Title>

        <ProductGrid products={products as IProduct[]} />

        <Pagination
          totalPages={numberOfPages}
          currentPage={humanPageNumber}
          onPageChange={onPageChange}
          createURL={createURL(categoryName)}
        />
      </Styles.Wrapper>
    </Layout>
  )
}

const Styles = {
  Wrapper: styled.div`
    ${tw`flex flex-col items-center px-4 pt-8 mx-auto md:px-0 xl:max-w-screen-xl`}

    & > *:not(:last-child) {
      ${tw`mb-6`}
    }
  `,
  Title: tw.h1`self-start mb-6 text-5xl font-bold`,
  Capitalize: tw.span`capitalize`,
}

export default CategoryTemplate

export const pageQuery = graphql`
  query DealsCategory($category: String!, $skip: Int!, $limit: Int!) {
    site {
      siteMetadata {
        title
      }
    }
    products: allMarkdownRemark(
      skip: $skip
      limit: $limit
      filter: {
        frontmatter: {
          categories: { eq: $category }
          price: { discount: { gt: 0 } }
        }
      }
    ) {
      edges {
        node {
          fields {
            slug
          }
          frontmatter {
            title
            id
            suffix
            price {
              original
              discount
            }
            image {
              childImageSharp {
                fluid {
                  ...GatsbyImageSharpFluid
                }
              }
            }
            description
          }
        }
      }
    }
  }
`
